import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, Copy } from "lucide-react";
import axios from "axios";
import { API } from "@/lib/api";
import { OrnamentalDivider } from "@/components/OrnamentalDivider";
import { toast } from "sonner";

export default function GiftVoucherSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [voucher, setVoucher] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sessionId) { setLoading(false); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/vouchers/by-session/${sessionId}`);
        setVoucher(data);
        if (data.payment_status === "paid" || tries > 8) {
          setLoading(false);
          return;
        }
      } catch (_) { /* keep polling */ }
      tries += 1;
      setTimeout(poll, 1500);
    };
    poll();
  }, [sessionId]);

  const copy = () => {
    if (!voucher?.code) return;
    navigator.clipboard.writeText(voucher.code);
    toast.success("Voucher code copied");
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="voucher-processing">
        <Loader2 className="mx-auto animate-spin text-[#6E2638]" size={40} />
        <div className="serif text-3xl text-[#6E2638] mt-6">Confirming your gift voucher…</div>
        <p className="text-[#1A1817]/70 mt-2 text-sm">This takes a moment while Stripe finalises the payment.</p>
      </div>
    );
  }

  if (!voucher) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <div className="serif text-3xl text-[#6E2638]">Voucher not found.</div>
        <Link to="/gift-vouchers" className="btn-primary mt-6 inline-flex">Try again</Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="voucher-success">
      <CheckCircle2 size={56} className="mx-auto text-[#1E4637]" />
      <div className="serif text-4xl md:text-5xl text-[#6E2638] mt-4">Your gift voucher is on its way.</div>
      <OrnamentalDivider label="Payment confirmed" />
      <p className="text-[#1A1817]/80">
        Thanks {voucher.buyer_name} — we've emailed the voucher PDF to <b>{voucher.buyer_email}</b>
        {voucher.recipient_email && <> and to <b>{voucher.recipient_email}</b></>}.
      </p>
      <div className="card-warm rounded-2xl p-8 mt-8 text-left">
        <div className="text-xs uppercase tracking-[0.24em] text-[#1E4637]">Voucher code</div>
        <div className="flex items-center justify-between mt-2 gap-4">
          <div className="serif text-3xl text-[#6E2638] font-mono" data-testid="voucher-code">{voucher.code}</div>
          <button onClick={copy} className="btn-outline-gold" data-testid="copy-voucher-code"><Copy size={16} /> Copy</button>
        </div>
        <div className="text-sm text-[#1A1817]/70 mt-4">Redeemable for <b>{voucher.seats} seat{voucher.seats > 1 ? "s" : ""}</b> · AUD ${voucher.total_price_aud.toFixed(2)} paid</div>
      </div>
      <div className="mt-8 flex gap-4 justify-center">
        <Link to="/" className="btn-outline-gold">Back to home</Link>
        <Link to="/individual" className="btn-primary">Book a walk</Link>
      </div>
    </div>
  );
}
